import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Broadcast, GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1652120268427-9ba66e870467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBkYXRhJTIwd2F2ZXN8ZW58MHx8fHwxNzg1OTE0OTQxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { user, setUser, loading } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? "Signed in" : "Account created");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-sv-bg">
      {/* Left: brand / visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-sv-border overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: `url(${LOGIN_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <Broadcast size={28} weight="bold" className="text-sv-live" />
          <span className="font-display font-black text-2xl tracking-tight uppercase">Sendaba</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display font-black text-5xl leading-none tracking-tight uppercase">
            Rate-limited<br />SMS<br />
            <span className="text-sv-live">command center</span>
          </h1>
          <p className="mt-6 text-sv-secondary text-sm leading-relaxed">
            Persistent background sending. Precise throttle control. Zero double-sends. Your campaigns keep running on
            the server, even after you close the browser.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-6 font-mono text-xs text-sv-muted">
          <span>PERSISTENT_WORKER</span>
          <span>·</span>
          <span>MSG/MIN_THROTTLE</span>
          <span>·</span>
          <span>DEDUPE_GUARANTEE</span>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm sv-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <Broadcast size={26} weight="bold" className="text-sv-live" />
            <span className="font-display font-black text-xl uppercase">Sendaba</span>
          </div>

          <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">
            {mode === "login" ? "// access terminal" : "// create operator"}
          </p>
          <h2 className="font-display font-bold text-3xl mt-2 tracking-tight">
            {mode === "login" ? "Sign in" : "Register"}
          </h2>

          <button
            data-testid="google-login-button"
            onClick={googleLogin}
            className="mt-8 w-full flex items-center justify-center gap-3 h-11 border border-sv-border bg-sv-surface hover:bg-sv-hover transition-colors duration-150 font-medium text-sm focus:outline-none focus:ring-1 focus:ring-sv-ink"
          >
            <GoogleLogo size={18} weight="bold" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-sv-border" />
            <span className="font-mono text-xs text-sv-muted">OR</span>
            <div className="h-px flex-1 bg-sv-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="font-mono text-xs text-sv-secondary uppercase">Name</label>
                <input
                  data-testid="register-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full h-11 bg-sv-surface border border-sv-border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
                  placeholder="Operator name"
                />
              </div>
            )}
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full h-11 bg-sv-surface border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 w-full h-11 bg-sv-surface border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-sv-ink text-sv-onink font-semibold text-sm flex items-center justify-center gap-2 hover:bg-sv-secondary transition-colors duration-150 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-sv-ink"
            >
              {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
              {!busy && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>

          <p className="mt-6 text-sm text-sv-secondary">
            {mode === "login" ? "No account yet?" : "Already registered?"}{" "}
            <button
              data-testid="toggle-auth-mode-button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-sv-live hover:underline font-medium"
            >
              {mode === "login" ? "Register" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
